import { useMemo } from 'react'
import type { HexItem } from '@/hooks/useHexItems'
import { nodeCount } from '@/views/hexView/layers'
import type { WorldFlowHop } from '@/analysis/worldFlowTypes'

/**
 * ONE counting semantics for every view.
 *
 * Totals always come from the server-aggregated `totalStreams` / `usersTotal`
 * carried on the nodes (via `nodeCount`, which prefers `totalStreams`), never
 * from `rows.length`. World sums hop totals; Hex sums node totals — after the
 * aggregator's canonical stream identity fix, these are the same numbers, so
 * clicking a country on World must show the same stream count in Hex.
 */
export type ViewCounts = {
  /** Number of nodes/tiles rendered (groups in studio/game mode, rows in stream mode). */
  itemCount: number
  /** Total distinct streams represented (server-aggregated, not rows.length). */
  totalStreams: number
  /** Total users represented. */
  usersTotal: number
}

export function countsForHexItems(items: HexItem[]): ViewCounts {
  let totalStreams = 0
  let usersTotal = 0
  for (const item of items) {
    totalStreams += nodeCount(item.tick)
    const users = (item.tick as { usersTotal?: number } | null | undefined)?.usersTotal
    if (typeof users === 'number' && Number.isFinite(users)) usersTotal += users
  }
  return { itemCount: items.length, totalStreams, usersTotal }
}

/** World-side totals from client hops (fallback: studio hops), same fields. */
export function countsForWorldHops(hops: WorldFlowHop[]): ViewCounts {
  const clients = hops.filter((h) => h.type === 'city')
  const source = clients.length ? clients : hops.filter((h) => h.type === 'studio')
  let totalStreams = 0
  let usersTotal = 0
  for (const h of source) {
    totalStreams += Number(h.totalStreams ?? 0)
    usersTotal += Number(h.usersTotal ?? 0)
  }
  return { itemCount: source.length, totalStreams, usersTotal }
}

export function useHexCounts(items: HexItem[]): ViewCounts {
  return useMemo(() => countsForHexItems(items), [items])
}
